import React from 'react'
import { Navigate } from 'react-router-dom'
import {
  Blocks,
  BookText,
  Boxes,
  Building,
  Building2,
  Cable,
  Calculator,
  CalendarRange,
  ChartColumn,
  ClipboardList,
  Coins,
  Database,
  FilePen,
  FileSearch,
  FileText,
  Gauge,
  Gem,
  HandCoins,
  Hash,
  House,
  Image as ImageIcon,
  KeyRound,
  Landmark,
  Layers,
  Link2,
  LayoutDashboard,
  ListOrdered,
  Network,
  Palette,
  PencilLine,
  Plug,
  Route as RouteIcon,
  Scale,
  ScrollText,
  Send,
  Settings,
  ShieldCheck,
  Shuffle,
  Sigma,
  SlidersHorizontal,
  Split,
  SquareFunction,
  Star,
  TrendingUp,
  Upload,
  UserRound,
  Users,
  UsersRound
} from 'lucide-react'
import LucideIcon from '../shared/components/LucideIcon'
import { AppRoute, Route } from './Route'
import { legacySettingsRedirectRoutes } from './legacySettingsRedirects'
import { lazyPage } from './lazyPage'
import { ContextFiltersSections } from '../features/contextFilters/types'
import { GroupSettingDataSource } from '../features/companyGroup/types/CompanyGroupSettings'
import SettingsRootRedirect from '../pages/settings/home/SettingsRootRedirect'
import { Authority } from '../utils/Authorizable/types'
import { ContractFranchise, ContractProduct } from '../types/contract/Contract'
import { Country } from '../types/company/Company'
import { voucherAccountingSoftware } from '../config/integrations/vouchers'
import AppShell from '../layouts/AppShell/AppShell'
import UserLayout from '../layouts/UserLayout'
// Kept eager: it must signal Puppeteer even when the page chunk fails to load
import PrintReportErrorPage from '../pages/reporting/custom/components/print/PrintReportErrorPage'
import Root from '../Root'
import AuthorityRouteGuard from '../components/Authorized/AuthorityRouteGuard'
import SettingLayout from '../layouts/SettingLayout'
import GroupGuard from '../pages/settings/company/group/GroupGuard'
import LayoutsProvider from '../pages/reporting/custom/components/layout/LayoutContext'
import { CalculatedReportProvider } from '../pages/reporting/custom/components/CalculatedReportContext'
import ProtectedRoute from '../components/ProtectedRoute/ProtectedRoute'
import {
  groupAuthority,
  groupBudgetAuthority,
  isEliminationCompany,
  isGroupCompany,
  multiCompany,
  showAccountorLayout,
  showKiinteistomaailmaLayout
} from './utils'
import {
  useCompaniesDashboards,
  useDimensionCompactDashboards,
  useDimensionDefaultDashboards,
  useUpdateCompaniesDashboardsOrder,
  useUpdateDimensionDashboardsOrder
} from '../features/dashboards'
import OrderPage from '../shared/components/OrderPage'
import Login from '../features/login/components/Login'

const SettingsHome = lazyPage(() => import('../pages/settings/home/SettingsHome'))
const ForgotPassword = lazyPage(() => import('../pages/user/forgotPassword/ForgotPassword'))
const IndustryAndCompetitorComparison = lazyPage(
  () => import('../pages/industryAndCompetitorComparison/IndustryAndCompetitorComparison')
)
const ResetPassword = lazyPage(() => import('../pages/user/resetPassword/ResetPassword'))
const ValueEvaluation = lazyPage(() => import('../pages/valuation/Valuation'))
const FundingApplications = lazyPage(() => import('../pages/fundingApplications/FundingApplications'))
const CompanyInformation = lazyPage(() => import('../pages/companyInformation/CompanyInformation'))
const DeliveryDownload = lazyPage(() => import('../components/Delivery/DeliveryDownload'))
const Vouchers = lazyPage(() => import('../pages/reporting/vouchers/Vouchers'))
const CompanyGeneralSettings = lazyPage(() => import('../pages/settings/company/general/CompanyGeneralSettings'))
const AppearanceSettings = lazyPage(() => import('../pages/settings/company/general/Appearance/AppearanceSettings'))
const Contracts = lazyPage(() => import('../components/Contracts/Contracts'))
const CustomReportOrder = lazyPage(() => import('../pages/reporting/custom/components/settings/CustomReportOrder'))
const AccountantAccountMap = lazyPage(() => import('../pages/settings/accountant/accountMap/AccountantAccountMap'))
const AccountantLogo = lazyPage(() => import('../pages/settings/accountant/general/AccountantLogo'))
const AccountorScorecardSubscriptions = lazyPage(
  () => import('../pages/settings/accountant/subscriptions/company-dashboard/AccountorScorecardSubscriptions')
)
const KiinteistomaailmaScorecardSubscriptions = lazyPage(
  () => import('../pages/settings/accountant/subscriptions/company-dashboard/KiinteistomaailmaScorecardSubscriptions')
)
const CompanyDashboardSubscriptions = lazyPage(
  () => import('../pages/settings/accountant/subscriptions/company-dashboard/CompanyDashboardSubscriptions')
)
const CustomReportsSubscriptions = lazyPage(
  () => import('../pages/settings/accountant/subscriptions/company-dashboard/CustomReportsSubscriptions')
)
const AccountantUsers = lazyPage(() => import('../pages/settings/accountant/users/AccountantUsers'))
const CompanyAccountMap = lazyPage(() => import('../pages/settings/company/accountMap/CompanyAccountMap'))
const BudgetDrivers = lazyPage(() => import('../pages/settings/company/budgeting/budgetDrivers/BudgetDrivers'))
const AccountCalculationSettingsFlat = lazyPage(
  () => import('../pages/settings/company/budgeting/accountCalculation/AccountCalculationSettingsFlat')
)
const DriversPage = lazyPage(() => import('../pages/budgeting/drivers/DriversPage'))
const BudgetGoogleSheets = lazyPage(
  () => import('../pages/settings/company/budgeting/budgetingImport/BudgetGoogleSheets')
)
const BudgetingCSVImport = lazyPage(() => import('../pages/settings/company/budgeting/budgetingImport/BudgetingImport'))
const BudgetOneDrive = lazyPage(() => import('../pages/settings/company/budgeting/budgetingImport/BudgetOneDrive'))
const BudgetingInput = lazyPage(() => import('../pages/settings/company/budgeting/budgetingInput/BudgetingInput'))
const BudgetingScenarios = lazyPage(() => import('../features/scenario/components/BudgetingScenarios'))
const Forecasts = lazyPage(() => import('../features/scenario/components/Forecasts'))
const DimensionSettingsPage = lazyPage(
  () => import('../pages/settings/company/dimensionSettings/DimensionSettingsPage')
)
const FiscalYears = lazyPage(() => import('../pages/settings/company/fiscalYears/FiscalYears'))
const ExchangeRatePage = lazyPage(() => import('../pages/settings/company/group/exchangeRate/ExchangeRatePage'))
const ExchangeRateEdit = lazyPage(() => import('../features/exchangeRate/components/ExchangeRateEdit'))
const ExchangeRateTable = lazyPage(() => import('../features/exchangeRate/components/ExchangeRateTable'))
const Figures = lazyPage(() => import('../pages/settings/company/group/figures/Figures'))
const Subsidiaries = lazyPage(() =>
  import('../pages/settings/company/group/subsidiaries/Subsidiaries').then(m => ({ default: m.Subsidiaries }))
)
const IntegrationSummary = lazyPage(() => import('../features/accountingIntegration/components/IntegrationSummary'))
const AccountingIntegrationPage = lazyPage(
  () => import('../pages/settings/company/integrations/accountingIntegration/AccountingIntegrationPage')
)
const CustomerMapping = lazyPage(() => import('../pages/settings/company/group/customerMapping/CustomerMapping'))
const CustomerMappingPair = lazyPage(
  () => import('../pages/settings/company/group/customerMapping/CustomerMappingPair')
)
const CustomerMappingWizard = lazyPage(
  () => import('../pages/settings/company/group/customerMapping/CustomerMappingWizard')
)
const AcPanther = lazyPage(() => import('../pages/settings/company/integrations/external/acPanther/AcPanther'))
const ExternalGoogleSheets = lazyPage(
  () => import('../pages/settings/company/integrations/external/ExternalGoogleSheets')
)
const Futursoft = lazyPage(() => import('../pages/settings/company/integrations/external/futursoft/Futursoft'))
const HrSuunti = lazyPage(() => import('../pages/settings/company/integrations/external/hrsuunti/HrSuunti'))
const Kiho = lazyPage(() => import('../pages/settings/company/integrations/external/kiho/Kiho'))
const Kotipizza = lazyPage(() => import('../pages/settings/company/integrations/external/kotipizza/Kotipizza'))
const Logisystems = lazyPage(() => import('../pages/settings/company/integrations/external/logisystems/Logisystems'))
const Mapon = lazyPage(() => import('../pages/settings/company/integrations/external/mapon/Mapon'))
const Maraplan = lazyPage(() => import('../pages/settings/company/integrations/external/maraplan/Maraplan'))
const ExternalOneDrive = lazyPage(
  () => import('../pages/settings/company/integrations/external/onedrive/ExternalOneDrive')
)
const Restolution = lazyPage(() => import('../pages/settings/company/integrations/external/restolution/Restolution'))
const Severa = lazyPage(() => import('../pages/settings/company/integrations/external/severa/Severa'))
const Tamigo = lazyPage(() => import('../pages/settings/company/integrations/external/tamigo/Tamigo'))
const Simphony = lazyPage(() => import('../pages/settings/company/integrations/external/simphony/Simphony'))
const Tulopos = lazyPage(() => import('../pages/settings/company/integrations/external/tulopos/Tulopos'))
const Formulas = lazyPage(() => import('../pages/settings/company/keyFigures/formulas/Formulas'))
const Subscribed = lazyPage(() => import('../pages/settings/company/keyFigures/subscribed/Subscribed'))
const PersonnelPage = lazyPage(() => import('../pages/settings/company/personnel/PersonnelPage'))
const ReallocationPage = lazyPage(() => import('../pages/settings/company/reallocation/ReallocationPage'))
const CompanyUsers = lazyPage(() => import('../pages/settings/company/users/CompanyUsers'))
const FinancierUsers = lazyPage(() => import('../pages/settings/financier/users/FinancierUsers'))
const UserGeneralSettings = lazyPage(() => import('../pages/settings/user/general/UserGeneralSettings'))
const Password = lazyPage(() => import('../pages/settings/user/password/Password'))
const Profile = lazyPage(() => import('../pages/settings/user/profile/Profile'))
const CashFlow = lazyPage(() => import('../pages/reporting/cashFlow/CashFlow'))
const CustomReportsPage = lazyPage(() => import('../pages/reporting/custom/CustomReportsPage'))
const KeyFigures = lazyPage(() => import('../pages/reporting/keyFigures/KeyFigures'))
const IcReconciliation = lazyPage(() => import('../pages/reporting/icReconciliation/IcReconciliation'))
const CompanyDashboard = lazyPage(() => import('../pages/dashboard/companyDashboard/CompanyDashboard'))
const MultiPurposeDashboardCompact = lazyPage(
  () => import('../pages/dashboard/multiPurposeDashboard/MultiPurposeDashboardCompact')
)
const MultiPurposeDashboardDefault = lazyPage(
  () => import('../pages/dashboard/multiPurposeDashboard/MultiPurposeDashboardDefault')
)
const SuperUserContract = lazyPage(() => import('../pages/superuser/contracts/components/SuperUserContract'))
const SuperUserContracts = lazyPage(() => import('../pages/superuser/contracts/SuperUserContracts'))
const Deliveries = lazyPage(() => import('../pages/superuser/deliveries/Deliveries'))
const Integration = lazyPage(() =>
  import('../pages/superuser/integration/Integration').then(m => ({ default: m.Integration }))
)
const SuperUserUser = lazyPage(() => import('../pages/superuser/users/components/SuperUserUser'))
const SuperUserUsers = lazyPage(() => import('../pages/superuser/users/SuperUserUsers'))
const Accounts = lazyPage(() => import('../features/account/components/Accounts'))
const LoanPage = lazyPage(() => import('../pages/budgeting/loans/LoanPage'))
const InvestmentsPage = lazyPage(() => import('../pages/budgeting/investments/InvestmentsPage'))
const PrintReportPage = lazyPage(() => import('../pages/reporting/custom/components/print/PrintReportPage'))
const CompanyDashboardOrderPage = lazyPage(() => import('../features/dashboards/components/CompanyDashboardOrderPage'))
const MultiCompanyDashboard = lazyPage(() => import('../pages/dashboard/multiCompanyDashboard/MultiCompanyDashboard'))
const RequirementForm = lazyPage(() => import('../features/jira/components/RequirementForm'))
const Ontime = lazyPage(() => import('../pages/settings/company/integrations/external/ontime/Ontime'))
const Nepton = lazyPage(() => import('../pages/settings/company/integrations/external/nepton/Nepton'))
const FinancialStatementsContainer = lazyPage(() =>
  import('../features/financialStatements').then(m => ({ default: m.FinancialStatementsContainer }))
)
const Tripletex = lazyPage(() => import('../pages/settings/company/integrations/accounting/Tripletex'))

// ei vielä käytössä, ei poisteta
// function RootBoundary() {
//   const error = useRouteError()

//   if (isRouteErrorResponse(error)) {
//     if (error.status === 404) {
//       return <div>This page doesnt exist!</div>
//     }

//     if (error.status === 401) {
//       return <div>You arent authorized to see this</div>
//     }

//     if (error.status === 503) {
//       return <div>Looks like our API is down</div>
//     }

//     if (error.status === 418) {
//       return <div>🫖</div>
//     }
//   }

//   return <div>Something went wrong</div>
// }

/** Budgeting settings pages need a budgeting product; a group company only when it budgets on group level. */
const budgetingSettingsAuthority: Authority[] = [
  {
    product: [ContractProduct.BASIC, ContractProduct.PRO],
    custom: () => !isGroupCompany()
  },
  {
    product: [ContractProduct.BASIC, ContractProduct.PRO],
    groupBudgetSource: GroupSettingDataSource.group,
    custom: isGroupCompany
  }
]

const routes: AppRoute[] = [
  {
    hideInMenu: true,
    element: <Root />,
    // errorElement: <RootBoundary />,
    routes: [
      {
        hideInMenu: true,
        element: <UserLayout />,
        routes: [
          {
            path: '/user/login',
            name: 'login',
            hideInMenu: true,
            element: <Login />
          },
          {
            path: '/user/forgot-password',
            name: 'forgot-password',
            hideInMenu: true,
            element: <ForgotPassword />
          },
          {
            path: '/user/reset-password',
            name: 'reset-password',
            hideInMenu: true,
            element: <ResetPassword extra="" />
          },
          {
            path: '/user/activate',
            name: 'activate',
            hideInMenu: true,
            element: <ResetPassword extra="usersPage:contact-finadeck" />
          }
        ]
      },
      {
        element: <ProtectedRoute />,
        routes: [
          {
            element: <AppShell />,
            routes: [
              // /dashboard
              {
                path: '/dashboard',
                handle: { docsPath: 'manual/businesses/dashboard' },
                name: 'dashboard',
                icon: <LucideIcon icon={LayoutDashboard} />,
                routes: [
                  {
                    path: '/dashboard/accountant',
                    name: 'accountant',
                    authority: { role: ['accountant-admin', 'accountant-user'] },
                    element: <MultiCompanyDashboard />
                  },
                  {
                    path: '/dashboard/financier',
                    name: 'financier',
                    authority: { role: ['financier-admin', 'financier-user'] },
                    element: <MultiCompanyDashboard />
                  },
                  {
                    path: '/dashboard/companies',
                    handle: { docsPath: 'manual/businesses/dashboard/companies' },
                    name: 'companies',
                    authority: {
                      role: ['company-admin', 'company-user'],
                      custom: multiCompany
                    },
                    element: <MultiCompanyDashboard />
                  },
                  {
                    path: '/dashboard/company',
                    name: 'company',
                    authority: {
                      role: [
                        'company-admin',
                        'company-user',
                        'accountant-admin',
                        'accountant-user',
                        'financier-admin',
                        'financier-user'
                      ]
                    },
                    element: <CompanyDashboard />,
                    handle: {
                      docsPath: 'manual/businesses/dashboard/company',
                      contextFilters: [
                        ContextFiltersSections.budgetScenario,
                        ContextFiltersSections.dimension,
                        ContextFiltersSections.periodGroups,
                        ContextFiltersSections.dataTypes,
                        ContextFiltersSections.emptyRows
                      ]
                    }
                  },
                  {
                    path: '/dashboard/dimensions',
                    handle: { docsPath: 'manual/businesses/dashboard/dimensions' },
                    name: 'dimensions',
                    authority: {
                      role: [
                        'company-admin',
                        'company-user',
                        'accountant-admin',
                        'accountant-user',
                        'financier-admin',
                        'financier-user'
                      ]
                    },
                    routes: [
                      {
                        path: '/dashboard/dimensions/default',
                        name: 'default',
                        element: <MultiPurposeDashboardDefault />,
                        handle: {
                          contextFilters: [
                            ContextFiltersSections.budgetScenario,
                            ContextFiltersSections.periodGroups,
                            ContextFiltersSections.dataTypes,
                            ContextFiltersSections.emptyRows
                          ]
                        }
                      },
                      {
                        path: '/dashboard/dimensions/compact',
                        name: 'compact',
                        element: <MultiPurposeDashboardCompact />,
                        handle: {
                          contextFilters: [ContextFiltersSections.budgetScenario, ContextFiltersSections.periodGroups]
                        }
                      }
                    ]
                  }
                ]
              },
              // /reporting
              {
                path: '/reporting',
                handle: { docsPath: 'manual/businesses/reporting' },
                name: 'reporting',
                icon: <LucideIcon icon={ChartColumn} />,
                routes: [
                  {
                    path: '/reporting/financial-statements',
                    name: 'financial-statements',
                    element: <FinancialStatementsContainer isBudgetingPage={false} />,
                    handle: {
                      contextFilters: [
                        ContextFiltersSections.budgetScenario,
                        ContextFiltersSections.dimension,
                        ContextFiltersSections.periodGroups,
                        ContextFiltersSections.dataTypes,
                        ContextFiltersSections.emptyRows
                      ]
                    }
                  },
                  {
                    path: '/reporting/key-figures',
                    name: 'key-figures',
                    handle: {
                      docsPath: 'manual/businesses/reporting/key-figures',
                      contextFilters: [
                        ContextFiltersSections.budgetScenario,
                        ContextFiltersSections.dimension,
                        ContextFiltersSections.periodGroups,
                        ContextFiltersSections.dataTypes,
                        ContextFiltersSections.emptyRows
                      ]
                    },
                    element: <KeyFigures />,
                    authority: {
                      role: [
                        'company-admin',
                        'company-user',
                        'accountant-admin',
                        'accountant-user',
                        'financier-admin',
                        'financier-user'
                      ]
                    }
                  },
                  {
                    path: '/reporting/cash-flow',
                    name: 'cash-flow',
                    handle: {
                      docsPath: 'manual/businesses/reporting/cash-flow',
                      contextFilters: [
                        ContextFiltersSections.budgetScenario,
                        ContextFiltersSections.dimension,
                        ContextFiltersSections.periodGroups,
                        ContextFiltersSections.dataTypes,
                        ContextFiltersSections.emptyRows
                      ]
                    },
                    element: <CashFlow />,
                    authority: {
                      role: [
                        'company-admin',
                        'company-user',
                        'accountant-admin',
                        'accountant-user',
                        'financier-admin',
                        'financier-user'
                      ]
                    }
                  },
                  {
                    path: '/reporting/custom',
                    name: 'custom',
                    handle: {
                      docsPath: 'manual/businesses/reporting/custom',
                      contextFilters: [ContextFiltersSections.emptyRows]
                    },
                    element: (
                      <CalculatedReportProvider>
                        <LayoutsProvider>
                          <CustomReportsPage />
                        </LayoutsProvider>
                      </CalculatedReportProvider>
                    )
                  }
                ]
              },
              // /budgeting
              {
                path: '/budgeting',
                handle: { docsPath: 'manual/businesses/budgeting' },
                name: 'budgeting',
                icon: <LucideIcon icon={Calculator} />,
                authority: {
                  role: [
                    'company-admin',
                    'company-user',
                    'accountant-admin',
                    'accountant-user',
                    'financier-admin',
                    'financier-user'
                  ],
                  product: [ContractProduct.BASIC, ContractProduct.PRO]
                },
                routes: [
                  {
                    path: '/budgeting/financial-statements',
                    name: 'financial-statements',
                    handle: {
                      contextFilters: [
                        ContextFiltersSections.budgetScenario,
                        ContextFiltersSections.dimension,
                        ContextFiltersSections.periodGroups,
                        ContextFiltersSections.dataTypes,
                        ContextFiltersSections.emptyRows
                      ]
                    },
                    element: <FinancialStatementsContainer isBudgetingPage />,
                    authority: groupBudgetAuthority()
                  },
                  {
                    path: '/budgeting/drivers',
                    name: 'drivers',
                    element: <DriversPage />,
                    handle: {
                      docsPath: 'manual/businesses/budgeting/drivers',
                      contextFilters: [
                        ContextFiltersSections.budgetScenario,
                        ContextFiltersSections.dimension,
                        ContextFiltersSections.periodGroups,
                        ContextFiltersSections.dataTypes
                      ]
                    }
                  },
                  {
                    path: '/budgeting/loans',
                    handle: { docsPath: 'manual/businesses/budgeting/balance-sheet/loans' },
                    name: 'loans',
                    authority: {
                      permission: 'read-root-dimension'
                    },
                    routes: [
                      {
                        path: '/budgeting/loans/existing',
                        name: 'existing',
                        element: <LoanPage isExisting />,
                        handle: {
                          contextFilters: [ContextFiltersSections.budgetScenario]
                        }
                      },
                      {
                        path: '/budgeting/loans/new',
                        name: 'new',
                        element: <LoanPage isExisting={false} />,
                        handle: {
                          contextFilters: [ContextFiltersSections.budgetScenario]
                        }
                      }
                    ]
                  }
                ]
              },
              {
                path: '/vouchers',
                name: 'vouchers',
                icon: <LucideIcon icon={FileSearch} />,
                element: <Vouchers />,
                handle: {
                  contextFilters: [ContextFiltersSections.dimension]
                },
                authority: [
                  {
                    permission: 'read-vouchers'
                  },
                  {
                    role: ['company-admin', 'accountant-admin', 'accountant-user'],
                    accountingSoftware: voucherAccountingSoftware
                  }
                ]
              },
              // /group
              {
                path: '/group',
                handle: { docsPath: 'manual/businesses/group-accounting' },
                name: 'group',
                icon: <LucideIcon icon={Network} />,
                element: <AuthorityRouteGuard />,
                authority: groupAuthority,
                routes: [
                  {
                    path: '/group/ic-reconciliation',
                    name: 'ic-reconciliation',
                    element: <IcReconciliation />
                  }
                ]
              },
              // /investments
              {
                path: '/investments',
                handle: { docsPath: 'manual/businesses/budgeting/balance-sheet/investments' },
                name: 'investments',
                icon: <LucideIcon icon={Boxes} />,
                authority: {
                  product: ContractProduct.PRO
                },
                routes: [
                  {
                    path: '/investments/existing',
                    name: 'existing',
                    element: <InvestmentsPage isExisting />,
                    handle: {
                      contextFilters: [ContextFiltersSections.budgetScenario]
                    }
                  },
                  {
                    path: '/investments/investment',
                    name: 'investment',
                    element: <InvestmentsPage isExisting={false} />,
                    handle: {
                      contextFilters: [ContextFiltersSections.budgetScenario]
                    }
                  }
                ]
              },
              {
                path: '/financing',
                name: 'financing',
                icon: <LucideIcon icon={HandCoins} />,
                authority: [
                  {
                    role: ['company-admin', 'accountant-admin'],
                    product: [ContractProduct.BASIC, ContractProduct.PRO],
                    custom: () => !isEliminationCompany()
                  }
                ],
                routes: [
                  {
                    path: '/financing/funding-applications',
                    name: 'funding-applications',
                    icon: <LucideIcon icon={FilePen} />,
                    element: <FundingApplications />
                  }
                ]
              },
              {
                path: '/comparison',
                handle: { docsPath: 'manual/businesses/comparison' },
                name: 'comparison',
                icon: <LucideIcon icon={Scale} />,
                element: <IndustryAndCompetitorComparison />,
                authority: [
                  {
                    role: [
                      'company-admin',
                      'company-user',
                      'accountant-admin',
                      'accountant-user',
                      'financier-admin',
                      'financier-user'
                    ],
                    country: Country.fi,
                    custom: () => !isEliminationCompany()
                  }
                ]
              },
              {
                path: '/valuation',
                name: 'valuation',
                icon: <LucideIcon icon={Gem} />,
                element: <ValueEvaluation />,
                handle: {
                  contextFilters: [
                    ContextFiltersSections.budgetScenario,
                    ContextFiltersSections.dimension,
                    ContextFiltersSections.periodGroups,
                    ContextFiltersSections.dataTypes,
                    ContextFiltersSections.emptyRows
                  ]
                },
                authority: [
                  {
                    role: [
                      'company-admin',
                      'company-user',
                      'accountant-admin',
                      'accountant-user',
                      'financier-admin',
                      'financier-user'
                    ],
                    product: [ContractProduct.BASIC, ContractProduct.PRO],
                    custom: () => !isEliminationCompany()
                  }
                ]
              },
              {
                path: '/company-information',
                name: 'companyInformation',
                icon: <LucideIcon icon={Database} />,
                element: <CompanyInformation />,
                authority: {
                  custom: () => !isEliminationCompany()
                }
              },

              // /settings
              {
                path: '/settings',
                name: 'settings',
                icon: <LucideIcon icon={Settings} />,
                hideInMenu: true,
                routes: [
                  {
                    index: true,
                    hideInMenu: true,
                    element: <SettingsRootRedirect />
                  },
                  {
                    path: '/settings/dashboards/company',
                    name: 'companies',
                    element: <SettingLayout />,
                    hideInMenu: true,
                    routes: [
                      {
                        path: '/settings/dashboards/company/order',
                        name: 'companies-order',
                        icon: <LucideIcon icon={ListOrdered} />,
                        element: <CompanyDashboardOrderPage />
                      }
                    ]
                  },
                  {
                    path: '/settings/dashboards/companies',
                    name: 'companies',
                    element: <SettingLayout />,
                    hideInMenu: true,
                    routes: [
                      {
                        path: '/settings/dashboards/companies/order',
                        name: 'companies-order',
                        icon: <LucideIcon icon={ListOrdered} />,
                        element: (
                          <OrderPage query={useCompaniesDashboards} mutation={useUpdateCompaniesDashboardsOrder} />
                        )
                      }
                    ]
                  },
                  {
                    path: '/settings/dashboards/dimensionsDefault',
                    name: 'dimensionsDefault',
                    element: <SettingLayout />,
                    hideInMenu: true,
                    routes: [
                      {
                        path: '/settings/dashboards/dimensionsDefault/order',
                        name: 'dimensionsDefault-order',
                        icon: <LucideIcon icon={ListOrdered} />,
                        element: (
                          <OrderPage
                            query={useDimensionDefaultDashboards}
                            mutation={useUpdateDimensionDashboardsOrder}
                          />
                        )
                      }
                    ]
                  },
                  {
                    path: '/settings/dashboards/dimensionsCompact',
                    name: 'dimensionsCompact',
                    element: <SettingLayout />,
                    hideInMenu: true,
                    routes: [
                      {
                        path: '/settings/dashboards/dimensionsCompact/order',
                        name: 'dimensionsCompact-order',
                        icon: <LucideIcon icon={ListOrdered} />,
                        element: (
                          <OrderPage
                            query={useDimensionCompactDashboards}
                            mutation={useUpdateDimensionDashboardsOrder}
                          />
                        )
                      }
                    ]
                  },
                  {
                    path: '/settings/custom-report',
                    handle: { docsPath: 'manual/businesses/reporting/custom' },
                    name: 'custom-report',
                    element: <SettingLayout />,
                    hideInMenu: true,
                    routes: [
                      {
                        path: '/settings/custom-report/order',
                        name: 'custom-report-order',
                        icon: <LucideIcon icon={ListOrdered} />,
                        element: <CustomReportOrder />
                      }
                    ]
                  },
                  {
                    path: '/settings/user',
                    handle: { docsPath: 'manual/businesses/settings/user' },
                    name: 'user',
                    element: <SettingLayout />,
                    routes: [
                      {
                        index: true,
                        path: '/settings/user',
                        name: 'home',
                        icon: <LucideIcon icon={House} />,
                        element: <SettingsHome />
                      },
                      {
                        path: '/settings/user/profile',
                        handle: { docsPath: 'manual/businesses/settings/user/profile' },
                        icon: <LucideIcon icon={UserRound} />,
                        name: 'profile',
                        element: <Profile />
                      },
                      {
                        path: '/settings/user/general',
                        icon: <LucideIcon icon={SlidersHorizontal} />,
                        name: 'general',
                        element: <UserGeneralSettings />
                      },
                      {
                        path: '/settings/user/password',
                        handle: { docsPath: 'manual/businesses/settings/user/password' },
                        icon: <LucideIcon icon={KeyRound} />,
                        name: 'password',
                        element: <Password />
                      }
                    ]
                  },
                  {
                    path: '/settings/accountant',
                    name: 'accountant',
                    element: <SettingLayout />,
                    authority: { role: ['accountant-admin', 'accountant-user'] },
                    routes: [
                      {
                        index: true,
                        path: '/settings/accountant',
                        name: 'home',
                        icon: <LucideIcon icon={House} />,
                        element: <SettingsHome />
                      },
                      {
                        path: '/settings/accountant/users',
                        name: 'users',
                        icon: <LucideIcon icon={Users} />,
                        element: <AccountantUsers />
                      },
                      {
                        path: '/settings/accountant/general',
                        name: 'general',
                        icon: <LucideIcon icon={Building2} />,
                        routes: [
                          {
                            path: '/settings/accountant/general/logoupload',
                            name: 'logoupload',
                            icon: <LucideIcon icon={ImageIcon} />,
                            element: <AccountantLogo />
                          }
                        ]
                      },
                      {
                        path: '/settings/accountant/contracts',
                        name: 'contracts',
                        icon: <LucideIcon icon={ScrollText} />,
                        element: <Contracts />
                      },
                      {
                        path: '/settings/accountant/account-map',
                        name: 'account-map',
                        icon: <LucideIcon icon={Split} />,
                        element: <AccountantAccountMap />
                      },
                      {
                        path: '/settings/accountant/subscriptions',
                        name: 'subscriptions',
                        icon: <LucideIcon icon={Send} />,
                        routes: [
                          {
                            path: '/settings/accountant/subscriptions/company-dashboard',
                            name: 'company-dashboard',
                            icon: <LucideIcon icon={LayoutDashboard} />,
                            element: <CompanyDashboardSubscriptions />
                          },
                          {
                            path: '/settings/accountant/subscriptions/custom-reports',
                            name: 'custom-reports',
                            icon: <LucideIcon icon={FileText} />,
                            element: <CustomReportsSubscriptions />
                          },
                          {
                            path: '/settings/accountant/subscriptions/accountor-scorecard',
                            name: 'accountor-scorecard',
                            icon: <LucideIcon icon={ClipboardList} />,
                            element: <AccountorScorecardSubscriptions />,
                            authority: {
                              custom: showAccountorLayout
                            }
                          },
                          {
                            path: '/settings/accountant/subscriptions/kiinteistomaailma-scorecard',
                            name: 'kiinteistomaailma-scorecard',
                            icon: <LucideIcon icon={ClipboardList} />,
                            element: <KiinteistomaailmaScorecardSubscriptions />,
                            authority: {
                              custom: showKiinteistomaailmaLayout
                            }
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: '/settings/financier',
                    name: 'accountant',
                    element: <SettingLayout />,
                    authority: { role: ['financier-admin', 'financier-user'] },
                    routes: [
                      {
                        index: true,
                        path: '/settings/financier',
                        name: 'home',
                        icon: <LucideIcon icon={House} />,
                        element: <SettingsHome />
                      },
                      {
                        path: '/settings/financier/users',
                        name: 'users',
                        icon: <LucideIcon icon={Users} />,
                        element: <FinancierUsers />
                      },
                      {
                        path: '/settings/financier/contracts',
                        name: 'contracts',
                        icon: <LucideIcon icon={ScrollText} />,
                        element: <Contracts />
                      },
                      {
                        path: '/settings/financier/account-map',
                        name: 'account-map',
                        icon: <LucideIcon icon={Split} />,
                        element: <AccountantAccountMap />
                      },
                      {
                        path: '/settings/financier/subscriptions',
                        name: 'subscriptions',
                        icon: <LucideIcon icon={Send} />,
                        routes: [
                          {
                            path: '/settings/financier/subscriptions/company-dashboard',
                            name: 'company-dashboard',
                            icon: <LucideIcon icon={LayoutDashboard} />,
                            element: <CompanyDashboardSubscriptions />
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: '/settings/company',
                    handle: { docsPath: 'manual/businesses/settings/company' },
                    name: 'company',
                    element: <SettingLayout />,
                    authority: {
                      role: [
                        'company-admin',
                        'company-user',
                        'accountant-admin',
                        'accountant-user',
                        'financier-admin',
                        'financier-user'
                      ]
                    },
                    routes: [
                      {
                        index: true,
                        path: '/settings/company',
                        name: 'home',
                        icon: <LucideIcon icon={House} />,
                        element: <SettingsHome />
                      },
                      {
                        path: '/settings/company/company-info',
                        name: 'company-info',
                        icon: <LucideIcon icon={Building2} />,
                        routes: [
                          {
                            path: '/settings/company/company-info/general',
                            name: 'general',
                            icon: <LucideIcon icon={SlidersHorizontal} />,
                            element: <CompanyGeneralSettings />
                          },
                          {
                            path: '/settings/company/company-info/appearance',
                            name: 'appearance',
                            icon: <LucideIcon icon={Palette} />,
                            element: <AppearanceSettings />
                          },
                          {
                            path: '/settings/company/company-info/users',
                            handle: { docsPath: 'manual/businesses/settings/company/users' },
                            name: 'users',
                            icon: <LucideIcon icon={Users} />,
                            element: <CompanyUsers />
                          }
                        ]
                      },
                      {
                        path: '/settings/company/integrations',
                        handle: { docsPath: 'manual/businesses/integrations' },
                        name: 'integrations',
                        icon: <LucideIcon icon={Plug} />,
                        homeSummary: <IntegrationSummary />,
                        routes: [
                          {
                            // Single page for all accounting software. The old per-software pages (integrations/accounting/*) are
                            // kept unrouted until this page has been tested, then removed
                            path: '/settings/company/integrations/accounting-integration',
                            handle: { docsPath: 'manual/businesses/integrations/accounting' },
                            name: 'accounting-integration',
                            icon: <LucideIcon icon={Cable} />,
                            element: <AccountingIntegrationPage />
                          },
                          {
                            // On the accounting integration page too; the old page stays reachable by its address during testing
                            path: '/settings/company/integrations/accounting/tripletex',
                            name: 'tripletex',
                            hideInMenu: true,
                            element: <Tripletex />
                          },
                          {
                            path: '/settings/company/integrations/external',
                            handle: { docsPath: 'manual/businesses/integrations/other' },
                            name: 'external',
                            icon: <LucideIcon icon={Blocks} />,
                            routes: [
                              {
                                path: '/settings/company/integrations/external/ac-panther',
                                name: 'acpanther',
                                element: <AcPanther />
                              },
                              {
                                path: '/settings/company/integrations/external/futursoft',
                                name: 'futursoft',
                                element: <Futursoft />
                              },
                              {
                                path: '/settings/company/integrations/external/google-sheets',
                                name: 'google-sheets',
                                element: <ExternalGoogleSheets />
                              },
                              {
                                path: '/settings/company/integrations/external/hrsuunti',
                                name: 'hrsuunti',
                                element: <HrSuunti />
                              },
                              {
                                path: '/settings/company/integrations/external/kiho',
                                name: 'kiho',
                                element: <Kiho />
                              },
                              {
                                path: '/settings/company/integrations/external/kotipizza',
                                name: 'kotipizza',
                                element: <Kotipizza />,
                                authority: {
                                  franchise: ContractFranchise.KOTIPIZZA
                                }
                              },
                              {
                                path: '/settings/company/integrations/external/logisystems',
                                name: 'logisystems',
                                element: <Logisystems />
                              },
                              {
                                path: '/settings/company/integrations/external/mapon',
                                name: 'mapon',
                                element: <Mapon />
                              },
                              {
                                path: '/settings/company/integrations/external/maraplan',
                                name: 'maraplan',
                                element: <Maraplan />
                              },
                              {
                                path: '/settings/company/integrations/external/onedrive',
                                name: 'external-onedrive',
                                element: <ExternalOneDrive />
                              },
                              {
                                path: '/settings/company/integrations/external/nepton',
                                name: 'nepton',
                                element: <Nepton />
                              },
                              {
                                path: '/settings/company/integrations/external/ontime',
                                name: 'ontime',
                                element: <Ontime />
                              },
                              {
                                path: '/settings/company/integrations/external/simphony',
                                name: 'simphony',
                                element: <Simphony />
                              },
                              {
                                path: '/settings/company/integrations/external/restolution',
                                name: 'restolution',
                                element: <Restolution />
                              },
                              {
                                path: '/settings/company/integrations/external/severa',
                                name: 'severa',
                                element: <Severa />
                              },
                              {
                                path: '/settings/company/integrations/external/tamigo',
                                name: 'tamigo',
                                element: <Tamigo />
                              },
                              {
                                path: '/settings/company/integrations/external/tulopos',
                                name: 'tulopos',
                                element: <Tulopos />
                              }
                            ]
                          }
                        ]
                      },
                      {
                        path: '/settings/company/financial',
                        name: 'financial',
                        icon: <LucideIcon icon={Landmark} />,
                        routes: [
                          {
                            path: '/settings/company/financial/fiscal-years',
                            handle: { docsPath: 'manual/businesses/settings/company/fiscal-years' },
                            name: 'fiscal-years',
                            icon: <LucideIcon icon={CalendarRange} />,
                            element: <FiscalYears />
                          },
                          {
                            path: '/settings/company/financial/personnel',
                            name: 'personnel',
                            icon: <LucideIcon icon={UsersRound} />,
                            element: <PersonnelPage />
                          },
                          {
                            path: '/settings/company/financial/dimensions',
                            handle: { docsPath: 'manual/businesses/settings/company/cost-centers' },
                            name: 'dimensions',
                            icon: <LucideIcon icon={Layers} />,
                            element: <DimensionSettingsPage />
                          },
                          {
                            path: '/settings/company/financial/accounts',
                            handle: { docsPath: 'manual/businesses/settings/company/accounts' },
                            name: 'accounts',
                            icon: <LucideIcon icon={BookText} />,
                            element: <Accounts />
                          },
                          {
                            path: '/settings/company/financial/account-map',
                            handle: { docsPath: 'manual/businesses/settings/company/chart-of-accounts' },
                            name: 'account-map',
                            icon: <LucideIcon icon={Split} />,
                            element: <CompanyAccountMap />
                          },
                          {
                            path: '/settings/company/financial/reallocation',
                            handle: { docsPath: 'manual/businesses/allocations' },
                            name: 'reallocation',
                            icon: <LucideIcon icon={Shuffle} />,
                            element: <ReallocationPage />,
                            authority: {
                              product: ContractProduct.PRO,
                              custom: () => !isGroupCompany()
                            }
                          }
                        ]
                      },
                      {
                        path: '/settings/company/budgeting',
                        handle: { docsPath: 'manual/businesses/settings/company/budgeting' },
                        name: 'budgeting',
                        icon: <LucideIcon icon={Calculator} />,
                        routes: [
                          {
                            path: '/settings/company/budgeting/budgeting-scenarios',
                            authority: budgetingSettingsAuthority,
                            name: 'budgeting-scenarios',
                            icon: <LucideIcon icon={RouteIcon} />,
                            element: <BudgetingScenarios />
                          },
                          {
                            path: '/settings/company/budgeting/forecasts',
                            handle: { docsPath: 'manual/businesses/forecasts' },
                            authority: budgetingSettingsAuthority,
                            name: 'forecasts',
                            icon: <LucideIcon icon={TrendingUp} />,
                            element: <Forecasts />
                          },
                          {
                            path: '/settings/company/budgeting/input',
                            authority: budgetingSettingsAuthority,
                            name: 'input',
                            icon: <LucideIcon icon={PencilLine} />,
                            element: <BudgetingInput />,
                            handle: {
                              contextFilters: [ContextFiltersSections.budgetScenario]
                            }
                          },
                          {
                            path: '/settings/company/budgeting/drivers',
                            handle: { docsPath: 'manual/businesses/budgeting/drivers/setup' },
                            authority: budgetingSettingsAuthority,
                            name: 'drivers',
                            icon: <LucideIcon icon={Gauge} />,
                            element: <BudgetDrivers />
                          },
                          {
                            path: '/settings/company/budgeting/account-calculation-flat',
                            authority: budgetingSettingsAuthority,
                            name: 'account-calculation-flat',
                            icon: <LucideIcon icon={Sigma} />,
                            element: <AccountCalculationSettingsFlat />,
                            handle: {
                              contextFilters: []
                            }
                          },
                          {
                            path: '/settings/company/budgeting/import',
                            handle: { docsPath: 'manual/businesses/budgeting/import' },
                            authority: budgetingSettingsAuthority,
                            name: 'import',
                            icon: <LucideIcon icon={Upload} />,
                            routes: [
                              {
                                path: '/settings/company/budgeting/import/csv',
                                handle: { docsPath: 'manual/businesses/budgeting/import/csv' },
                                name: 'csv',
                                element: <BudgetingCSVImport />
                              },
                              {
                                path: '/settings/company/budgeting/import/google-sheet',
                                handle: { docsPath: 'manual/businesses/budgeting/import/google-sheets' },
                                name: 'google-sheet',
                                element: <BudgetGoogleSheets />
                              },
                              {
                                path: '/settings/company/budgeting/import/onedrive',
                                handle: { docsPath: 'manual/businesses/budgeting/import/onedrive' },
                                name: 'import-onedrive',
                                element: <BudgetOneDrive />
                              }
                            ]
                          }
                        ]
                      },
                      {
                        path: '/settings/company/key-figures',
                        handle: { docsPath: 'manual/businesses/settings/company/key-figures' },
                        name: 'key-figures',
                        icon: <LucideIcon icon={ChartColumn} />,
                        routes: [
                          {
                            path: '/settings/company/key-figures/subscribed',
                            handle: { docsPath: 'manual/businesses/settings/company/key-figures/subscribed' },
                            name: 'subscribed',
                            icon: <LucideIcon icon={Star} />,
                            element: <Subscribed />
                          },
                          {
                            path: '/settings/company/key-figures/formulas',
                            handle: { docsPath: 'manual/businesses/settings/company/key-figures/custom' },
                            name: 'formulas',
                            icon: <LucideIcon icon={SquareFunction} />,
                            element: <Formulas />
                          }
                        ]
                      },
                      {
                        path: '/settings/company/report-distribution',
                        name: 'report-distribution',
                        icon: <LucideIcon icon={Send} />,
                        authority: {
                          permission: 'read-root-dimension'
                        },
                        routes: [
                          {
                            path: '/settings/company/report-distribution/company-dashboard',
                            name: 'company-dashboard',
                            icon: <LucideIcon icon={LayoutDashboard} />,
                            element: <CompanyDashboardSubscriptions />
                          },
                          {
                            path: '/settings/company/report-distribution/custom-reports',
                            name: 'custom-reports',
                            icon: <LucideIcon icon={FileText} />,
                            element: <CustomReportsSubscriptions />
                          },
                          {
                            path: '/settings/company/report-distribution/accountor-scorecard',
                            name: 'accountor-scorecard',
                            icon: <LucideIcon icon={ClipboardList} />,
                            element: <AccountorScorecardSubscriptions />,
                            authority: {
                              custom: showAccountorLayout
                            }
                          },
                          {
                            path: '/settings/company/report-distribution/kiinteistomaailma-scorecard',
                            name: 'kiinteistomaailma-scorecard',
                            icon: <LucideIcon icon={ClipboardList} />,
                            element: <KiinteistomaailmaScorecardSubscriptions />,
                            authority: {
                              custom: showKiinteistomaailmaLayout
                            }
                          }
                        ]
                      },
                      {
                        path: '/settings/company/group',
                        handle: { docsPath: 'manual/businesses/group-accounting/structure' },
                        name: 'group',
                        icon: <LucideIcon icon={Network} />,
                        element: <GroupGuard />,
                        authority: {
                          custom: isGroupCompany
                        },
                        routes: [
                          {
                            path: '/settings/company/group/subsidiaries',
                            name: 'subsidiaries',
                            icon: <LucideIcon icon={Building} />,
                            element: <Subsidiaries />
                          },
                          {
                            path: '/settings/company/group/customer-mapping',
                            name: 'customer-mapping',
                            icon: <LucideIcon icon={Link2} />,
                            routes: [
                              {
                                index: true,
                                hideInMenu: true,
                                element: <CustomerMapping />
                              },
                              {
                                path: '/settings/company/group/customer-mapping/pair',
                                name: 'pair',
                                hideInMenu: true,
                                element: <CustomerMappingPair />
                              },
                              {
                                path: '/settings/company/group/customer-mapping/wizard',
                                name: 'wizard',
                                hideInMenu: true,
                                element: <CustomerMappingWizard />
                              }
                            ]
                          },
                          {
                            path: '/settings/company/group/figures',
                            name: 'figures',
                            icon: <LucideIcon icon={Hash} />,
                            element: <Figures />
                          },
                          {
                            path: '/settings/company/group/exchangeRate',
                            handle: { docsPath: 'manual/businesses/group-accounting/currencies' },
                            name: 'exchangeRate',
                            icon: <LucideIcon icon={Coins} />,
                            element: <ExchangeRatePage />,
                            routes: [
                              {
                                index: true,
                                hideInMenu: true,
                                element: <ExchangeRateTable />
                              },
                              {
                                path: '/settings/company/group/exchangeRate/edit',
                                name: 'edit',
                                hideInMenu: true,
                                element: <ExchangeRateEdit />
                              }
                            ]
                          }
                        ]
                      },
                      ...legacySettingsRedirectRoutes
                    ]
                  }
                ]
              },
              {
                path: '/deliveries/:uuid',
                name: 'deliveries',
                element: <DeliveryDownload />,
                hideInMenu: true
              },
              // /superuser
              {
                path: '/superuser',
                name: 'superuser',
                icon: <LucideIcon icon={ShieldCheck} />,
                element: <SettingLayout />,
                authority: { role: 'superuser' },
                routes: [
                  {
                    path: '/superuser/contracts',
                    name: 'contracts',
                    element: <SuperUserContracts />
                  },
                  {
                    path: '/superuser/contracts/:id',
                    name: 'contract',
                    element: <SuperUserContract />,
                    hideInMenu: true
                  },
                  {
                    path: '/superuser/users',
                    name: 'users',
                    element: <SuperUserUsers />
                  },
                  {
                    path: '/superuser/users/:id',
                    name: 'user',
                    element: <SuperUserUser />,
                    hideInMenu: true
                  },
                  {
                    path: '/superuser/integration',
                    name: 'integration',
                    element: <Integration />
                  },
                  {
                    path: '/superuser/deliveries',
                    name: 'deliveries',
                    element: <Deliveries />
                  },
                  {
                    path: '/superuser/requirementSpecification',
                    name: 'requirementSpecification',
                    element: <RequirementForm />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: '/print/report/:reportId',
        name: 'print-report',
        loader: async args => {
          const { printReportLoader } = await import('../pages/reporting/custom/components/print/PrintReportPage')
          return printReportLoader(args)
        },
        element: <PrintReportPage />,
        errorElement: <PrintReportErrorPage />,
        hideInMenu: true
      },
      {
        path: '*',
        element: <Navigate to="/user/login" />,
        hideInMenu: true
      }
    ]
  }
]

export default routes as Route[]
